import {
    Box,
    Flex,
    Heading,
    Select,
    Spinner,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useColorModeValue,
    useToast,
} from '@chakra-ui/react';
import { MdTune } from 'react-icons/md';
import { useState } from 'react';
import Card from 'components/card/Card';
import { typography } from 'theme/tokens';
import { translate } from '../../i18n';
import EmptyState from '../../components/states/EmptyState';
import ErrorState from '../../components/states/ErrorState';
import { useElevatedAdminAccess } from '../organization/hooks/useIsElevatedAdmin';
import ElevationUnknownNotice from '../_shared/ElevationUnknownNotice';
import { useGetRoadmapClientMapping } from './hooks/useGetRoadmapClientMapping';
import { useSetOrgClientLabel } from './hooks/useSetOrgClientLabel';

export default function RoadmapMappingPage() {
    const { state: elevationState, isElevated: isEnabled } = useElevatedAdminAccess();
    const checkingElevated = elevationState === 'checking';

    const { data, isLoading, isError, refetch } =
        useGetRoadmapClientMapping(isEnabled);

    const toast = useToast();
    const [savingId, setSavingId] = useState<string | null>(null);

    const { mutate } = useSetOrgClientLabel({
        onSuccess: () => {
            setSavingId(null);
            toast({
                title: translate('roadmap.mapping_saved'),
                status: 'success',
                duration: 2000,
                position: 'top-right',
            });
        },
        onError: () => {
            setSavingId(null);
            toast({
                title: translate('roadmap.mapping_save_error'),
                status: 'error',
                duration: 3000,
                position: 'top-right',
            });
        },
    });

    const textColor = useColorModeValue('secondaryGray.900', 'white');
    const mutedColor = useColorModeValue('secondaryGray.600', 'secondaryGray.500');
    const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

    const handleChange = (organizationId: string, value: string) => {
        setSavingId(organizationId);
        mutate({ organizationId, linearClientLabelId: value || null });
    };

    if (checkingElevated) {
        return (
            <Flex justify="center" py={10}>
                <Spinner color="brand.500" />
            </Flex>
        );
    }

    // An elevation read that did not come back is NOT a refusal. This page used
    // to answer both with the `restricted` EmptyState below — the only surface
    // in this class that stated the denial in words rather than by vanishing,
    // and therefore the only one an operator could actively be misled by: a
    // platform admin whose one unretried `is-elevated-admin` call failed was
    // told, in a sentence, that this page was not for them. `staleTime:
    // Infinity` means that verdict then stood for the rest of the session.
    if (elevationState === 'unknown') {
        return (
            <Card>
                <ElevationUnknownNotice />
            </Card>
        );
    }

    if (!isEnabled) {
        return (
            <Card>
                <EmptyState
                    icon={MdTune}
                    title={translate('roadmap.mapping_restricted_title')}
                    description={translate('roadmap.mapping_restricted_desc')}
                />
            </Card>
        );
    }

    return (
        <Card flexDirection="column" w="100%">
            <Flex px="25px" mb="8px" direction="column" gap="2px">
                <Heading as="h2" color={textColor} {...typography.pageTitle}>
                    {translate('roadmap.mapping_title')}
                </Heading>
                <Text color={mutedColor} fontSize="sm">
                    {translate('roadmap.mapping_subtitle')}
                </Text>
            </Flex>

            <Box px="25px" py="12px">
                {isLoading ? (
                    <Flex justify="center" py={10}>
                        <Text color="gray.400">{translate('roadmap.loading')}</Text>
                    </Flex>
                ) : isError ? (
                    <ErrorState
                        message={translate('roadmap.error_loading')}
                        onRetry={refetch}
                    />
                ) : (
                    <>
                        {data && data.labels.length === 0 && (
                            <Text color="orange.400" fontSize="sm" mb="12px">
                                {translate('roadmap.mapping_no_labels')}
                            </Text>
                        )}
                        <Table>
                            <Thead>
                                <Tr>
                                    <Th borderColor={borderColor}>
                                        {translate('roadmap.mapping_org')}
                                    </Th>
                                    <Th borderColor={borderColor}>
                                        {translate('roadmap.mapping_label')}
                                    </Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {data?.organizations.map((org) => (
                                    <Tr key={org.id}>
                                        <Td borderColor={borderColor}>
                                            <Text
                                                color={textColor}
                                                fontSize="sm"
                                                fontWeight="600"
                                            >
                                                {org.name}
                                            </Text>
                                        </Td>
                                        <Td borderColor={borderColor}>
                                            <Flex align="center" gap={2}>
                                                <Select
                                                    maxW="280px"
                                                    size="sm"
                                                    value={org.linearClientLabelId ?? ''}
                                                    isDisabled={savingId === org.id}
                                                    onChange={(e) =>
                                                        handleChange(org.id, e.target.value)
                                                    }
                                                    borderColor={borderColor}
                                                >
                                                    <option value="">
                                                        {translate('roadmap.mapping_unassigned')}
                                                    </option>
                                                    {org.linearClientLabelId &&
                                                        !data.labels.some(
                                                            (l) =>
                                                                l.id ===
                                                                org.linearClientLabelId,
                                                        ) && (
                                                            <option
                                                                value={
                                                                    org.linearClientLabelId
                                                                }
                                                            >
                                                                {'⚠ '}
                                                                {translate(
                                                                    'roadmap.mapping_deleted_label',
                                                                )}
                                                            </option>
                                                        )}
                                                    {data.labels.map((label) => (
                                                        <option key={label.id} value={label.id}>
                                                            {label.name}
                                                        </option>
                                                    ))}
                                                </Select>
                                                {savingId === org.id && (
                                                    <Spinner size="sm" color="brand.500" />
                                                )}
                                            </Flex>
                                        </Td>
                                    </Tr>
                                ))}
                            </Tbody>
                        </Table>
                    </>
                )}
            </Box>
        </Card>
    );
}
